import { t } from "i18next";
import * as React from "react";
import InspectionDrawing from "../../dto/components/InspectionDrawing";
import InspectionTileLayer from "../../dto/components/InspectionTileLayer";
import CatalogBase, { ICatalogBaseProps, ICatalogBaseState } from "./CatalogBase";
import { ICatalogItemProps } from "./CatalogItem";
import CatalogLayerGroup from "./CatalogLayerGroup";
import CatalogLayerGroupSingleChoice from "./CatalogLayerGroupSingleChoice";

export interface ICatalogProps extends ICatalogBaseProps{
    inspectionDrawings: InspectionDrawing[];
    selectedInspectionDrawingId: string;
    onInspectionDrawingSelectionChange: (id: string) => void;

    tileLayers: InspectionTileLayer[];
    selectedTileLayerId: string;
    onTileLayerSelectionChange: (id: string) => void;

    blockDefinitionSelected: boolean;
    onBlockDefinitionSelectionChange: () => void;
    classificationDataSelected: boolean;
    onClassificationDataSelectionChange: () => void;
    sectorDefinitionSelected: boolean;
    onSectorDefinitionSelectionChange: () => void;
    theoreticalSectionsSelected: boolean;
    onTheoreticalSectionsSelectionChange: () => void;
}

export default class CatalogLayerAnalysis extends CatalogBase<ICatalogProps, ICatalogBaseState> {

    public getInitialState(): ICatalogBaseState {
        const initialState = super.getInitialState();
        initialState.isExpanded = true;
        return initialState;
    }

    public renderChildren = () => {
        return (
            <React.Fragment>
                <CatalogLayerGroupSingleChoice
                    name={t("labelImages")}
                    items={this.getImageItems()}
                    marginRight={0}
                />
                <CatalogLayerGroupSingleChoice
                    name={t("labelInspectionDrawings")}
                    items={this.getDrawingItems()}
                    marginRight={0}
                />
                <CatalogLayerGroup
                    name={t("labelDesign")}
                    items={[
                        {
                            name: t("labelBlockDefinition"),
                            isHidden: !this.props.blockDefinitionSelected,
                            onVisibilityToggle: this.props.onBlockDefinitionSelectionChange,
                            testId: "toggle-block-definition-button",
                        },
                        {
                            name: t("labelSectorDefinition"),
                            isHidden: !this.props.sectorDefinitionSelected,
                            onVisibilityToggle: this.props.onSectorDefinitionSelectionChange,
                            testId: "toggle-sector-definition-button",
                        },
                        {
                            name: t("labelTheoreticalSections"),
                            isHidden: !this.props.theoreticalSectionsSelected,
                            onVisibilityToggle: this.props.onTheoreticalSectionsSelectionChange,
                            testId: "toggle-theoretical-section-button",
                        },
                    ]}
                />
                <CatalogLayerGroup
                    name={t("labelClassification")}
                    items={[{
                        name: t("labelClassification"),
                        isHidden: !this.props.classificationDataSelected,
                        onVisibilityToggle: this.props.onClassificationDataSelectionChange,
                        testId: "toggle-classification-button",
                    }]}
                />
            </React.Fragment>
        )
    }

    private getDrawingItems = (): ICatalogItemProps[] => {
        return this.props.inspectionDrawings.map(drawing => {
            const isHidden = this.props.selectedInspectionDrawingId !== drawing.id;
            return {
                name: drawing.name,
                id: drawing.id,
                isHidden,
                onVisibilityToggle: this.getInspectionDrawingSelectionMethod(drawing.id),
                testId: `item-id?${drawing.id}`,
            }}
        )
    };

    private getImageItems = () => {
        return this.props.tileLayers.map(tileLayer => {
            const isHidden = this.props.selectedTileLayerId !== tileLayer.id;
            return {
                name: tileLayer.name,
                id: tileLayer.id,
                isHidden,
                onVisibilityToggle: this.getTileLayerSelectionMethod(tileLayer.id),
                testId: `item-id?${tileLayer.id}`,
            }}
        )
    };

    private getInspectionDrawingSelectionMethod = (id: string) => {
        return () => {this.props.onInspectionDrawingSelectionChange(id)}
    }

    private getTileLayerSelectionMethod = (id: string) => {
        return () => {this.props.onTileLayerSelectionChange(id)}
    }
}